import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@ariakit/react';
import type { Table } from '@tanstack/react-table';
import { exportToCSV } from '../utils/export';

interface ExportButtonProps<TData> {
  table: Table<TData>;
  filename?: (data: any[]) => string;
  onExport?: (data: any[]) => void;
  exportTransform?: (data: any[]) => any[];
  className?: string;
}

export default function ExportButton<TData>({
  table,
  filename,
  onExport,
  exportTransform,
  className = '',
}: ExportButtonProps<TData>) {
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExport = async () => {
    if (onExport) {
      const rows = table.getFilteredRowModel().rows;
      const data = rows.map(row => row.original);
      const transformedData = exportTransform ? exportTransform(data) : data;
      onExport(transformedData);
      return;
    }
    
    setIsExporting(true);
    
    try {
      const result = await exportToCSV(table, {
        filename,
        transform: exportTransform,
      });
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  const rowCount = table.getFilteredRowModel().rows.length;
  const isDisabled = rowCount === 0 || isExporting;
  
  return (
    <Button
      onClick={handleExport}
      disabled={isDisabled}
      className={`
        inline-flex items-center gap-2 px-4 py-2 
        text-sm font-medium rounded-md
        border border-gray-300 bg-white text-gray-700
        hover:bg-gray-50 hover:text-gray-900
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200
        ${className}
      `}
      title={isDisabled ? 'No data to export' : `Export ${rowCount} rows to CSV`}
    >
      <Download className={`h-4 w-4 ${isExporting ? 'animate-pulse' : ''}`} />
      <span>{isExporting ? 'Exporting...' : 'Export CSV'}</span>
    </Button>
  );
}
